import type {
  ListMbcAdminAssignmentsResponse,
  MbcAdminGuideline,
} from "@nlc/shared-types";

import { useState } from "react";

import { Button } from "@/components/button";
import { Label, TextArea, TextField } from "@/components/field";
import { Link } from "@/components/link";
import { toast } from "@/components/toast/toast-queue";

import { useAssignGuideline } from "./design-types/hooks/useAssignGuideline";
import {
  assignmentLayers,
  type AssignmentSource,
  type GuidelineScope,
  resolveEffectiveAssignment,
} from "./guideline-scope";
import { GuidelineSelect } from "./GuidelineSelect";

interface GuidelineAssignmentProps {
  readonly assignments: ListMbcAdminAssignmentsResponse;
  readonly guidelines: MbcAdminGuideline[];
  readonly onClose: () => void;
  readonly scope: GuidelineScope;
  readonly subCategoryName?: string;
}

export function GuidelineAssignment({
  assignments,
  guidelines,
  onClose,
  scope,
  subCategoryName,
}: GuidelineAssignmentProps) {
  const [savedLayer, ...inheritedLayers] = assignmentLayers(assignments, scope);
  const [guidelineId, setGuidelineId] = useState(savedLayer!.guidelineId);
  const [notes, setNotes] = useState(savedLayer!.notes ?? "");
  const [problems, setProblems] = useState<string[]>([]);
  const assignGuideline = useAssignGuideline();

  const trimmedNotes = notes.trim() || null;
  const isDirty =
    guidelineId !== savedLayer!.guidelineId ||
    trimmedNotes !== savedLayer!.notes;
  const effective = resolveEffectiveAssignment(guidelines, [
    { guidelineId, notes: trimmedNotes, source: "own" },
    ...inheritedLayers,
  ]);

  const sourceLabel: Record<AssignmentSource, string> = {
    default: "the default",
    own: `this ${scope.kind === "designType" ? "design type" : "subcategory"}`,
    subCategory: `subcategory "${subCategoryName}"`,
  };

  const handleSave = () =>
    assignGuideline.mutate(
      { guidelineId, notes: trimmedNotes, scope },
      {
        onError: (error) => {
          setProblems(error.message.split("\n"));
          toast.add({
            description: error.message,
            title: "Could not save the guideline",
            type: "error",
          });
        },
        onSuccess: () => {
          toast.add({
            title: `Guideline saved for ${scope.name}`,
            type: "success",
          });
          onClose();
        },
      },
    );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-md font-semibold text-text-primary">Guideline</h2>
        <p className="text-sm text-text-tertiary">
          The guideline and the notes are each inherited independently when left
          empty: design type, then subcategory, then the default.
        </p>
      </div>

      <GuidelineSelect
        allowInherit
        guidelines={guidelines}
        onChange={setGuidelineId}
        value={guidelineId}
      />

      <TextField onChange={setNotes} value={notes}>
        <Label>Notes for the agent</Label>
        <TextArea rows={3} />
      </TextField>

      <dl className="flex flex-col gap-2 rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm">
        <div>
          <dt className="inline text-text-tertiary">Effective guideline: </dt>
          <dd className="inline">
            {effective.guideline ? (
              <>
                <Link
                  className="text-sm font-medium"
                  href={`/admin/mbc/guidelines/${effective.guideline.value._id}`}
                >
                  {effective.guideline.value.name}
                </Link>{" "}
                <span className="text-text-tertiary">
                  (from {sourceLabel[effective.guideline.source]})
                </span>
              </>
            ) : (
              "none"
            )}
          </dd>
        </div>
        <div>
          <dt className="inline text-text-tertiary">Effective notes: </dt>
          <dd className="inline">
            {effective.notes ? (
              <>
                {effective.notes.value}{" "}
                <span className="text-text-tertiary">
                  (from {sourceLabel[effective.notes.source]})
                </span>
              </>
            ) : (
              "none"
            )}
          </dd>
        </div>
      </dl>

      {problems.length > 0 && (
        <ul className="text-sm text-text-error" role="alert">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end gap-5 border-t border-border-primary pt-4">
        <Button onPress={onClose} variant="plain">
          Cancel
        </Button>
        <Button
          isDisabled={!isDirty || assignGuideline.isPending}
          onPress={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
